import React, { useMemo } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, Text } from './ui'
import { getAvailableModuleIcons } from '../utils/moduleIcons'
import { groupModuleIconsByCategory } from '../utils/moduleIconCategories'

type ModuleIconPickerModalProps = {
  open: boolean
  selectedIcon?: string
  onClose: () => void
  onSelect: (iconName: string) => void
}

const availableIcons = getAvailableModuleIcons()

const ModuleIconPickerModal: React.FC<ModuleIconPickerModalProps> = ({
  open,
  selectedIcon,
  onClose,
  onSelect,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('')

  const filteredGroups = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    const icons = term
      ? availableIcons.filter(({ name }) => name.toLowerCase().includes(term))
      : availableIcons

    return groupModuleIconsByCategory(icons)
  }, [searchTerm])

  const totalIcons = useMemo(
    () => filteredGroups.reduce((sum, group) => sum + group.icons.length, 0),
    [filteredGroups]
  )

  const handleClose = () => {
    setSearchTerm('')
    onClose()
  }

  const handleSelect = (iconName: string) => {
    setSearchTerm('')
    onSelect(iconName)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <Heading as="h3" size="2xl">Selecione um ícone</Heading>
          <Button onClick={handleClose} variant="ghost">
            <XMarkIcon className="h-6 w-6" />
          </Button>
        </div>

        <div className="mb-4">
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar ícone por nome..."
            className="w-full"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {totalIcons === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Text size="sm">Nenhum ícone encontrado para &quot;{searchTerm}&quot;</Text>
            </div>
          ) : (
            <div className="space-y-6 p-2">
              {filteredGroups.map((group) => (
                <section key={group.id}>
                  <div className="sticky top-0 z-10 mb-3 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm py-1">
                    <Heading as="h4" size="xl" className="text-gray-700 dark:text-gray-200">
                      {group.label}
                    </Heading>
                    <Text size="xs" className="text-gray-500 dark:text-gray-400">
                      {group.icons.length} ícone{group.icons.length !== 1 ? 's' : ''}
                    </Text>
                  </div>

                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3">
                    {group.icons.map(({ name, component: IconComponent }) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => handleSelect(name)}
                        className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          selectedIcon === name
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-700'
                        }`}
                        title={name}
                      >
                        <IconComponent className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                        <Text size="xs" className="mt-1 text-center text-gray-600 dark:text-gray-400 truncate w-full">
                          {name}
                        </Text>
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-between items-center">
          <Text size="sm" className="text-gray-500 dark:text-gray-400">
            {totalIcons} ícone{totalIcons !== 1 ? 's' : ''} encontrado{totalIcons !== 1 ? 's' : ''}
            {filteredGroups.length > 1 ? ` em ${filteredGroups.length} categorias` : ''}
          </Text>
          <Button variant="ghost" onClick={handleClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ModuleIconPickerModal
